'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Whether the Google sign-in feature is enabled.
 *
 * Enabled iff the `GOOGLE_CLIENT_ID` env var is set to a non-empty value.
 * Disabled when it is absent or an empty string — in that case the root layout
 * does not render `<GoogleOAuthProvider>`, so no Google Identity Services
 * scripts are loaded.
 *
 * This context is intentionally decoupled from `@react-oauth/google`: the
 * Google OAuth context is only mounted when enabled (and its hooks throw
 * without a provider), so consumers need an independent signal for "enabled".
 */
const GoogleAuthEnabledContext = createContext(false);

export function GoogleAuthFeatureProvider({
    enabled,
    children
}: {
    enabled: boolean;
    children: ReactNode;
}) {
    return (
        <GoogleAuthEnabledContext.Provider value={enabled}>
            {children}
        </GoogleAuthEnabledContext.Provider>
    );
}

/**
 * Returns whether Google auth is enabled.
 *
 * Disabled when `GOOGLE_CLIENT_ID` is absent or empty. Must be used within
 * `<GoogleAuthFeatureProvider>` (mounted by the root layout).
 */
export function useGoogleAuthEnabled(): boolean {
    return useContext(GoogleAuthEnabledContext);
}
